import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, MapPin, MessageCircle, Calendar, SlashIcon } from "lucide-react";
import { WebAvatar } from "@/components/WebAvatar";
import { useAuth } from "@/hooks/use-auth";

interface UserProfile {
  id: number;
  name: string;
  town?: string | null;
  bio?: string | null;
  avatarUrl?: string | null;
  role: string;
  isModerator: boolean;
  isJournalist: boolean;
  commentsMade: number;
  createdAt: string;
  posts: Array<{
    id: number;
    content: string;
    town: string;
    reactions: Record<string, number>;
    commentsCount: number;
    createdAt: string;
    imageUrl?: string | null;
  }>;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

function formatTimeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const d = Math.floor(diff / 86400000);
  if (d === 0) return "Today";
  if (d === 1) return "Yesterday";
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function CommunityProfile() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { user } = useAuth();

  const [isBlocked, setIsBlocked] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);

  const { data: profile, isLoading, error } = useQuery<UserProfile>({
    queryKey: ["user-profile", params.id],
    queryFn: () => fetch(`/api/users/${params.id}/profile`).then((r) => r.json()) as Promise<UserProfile>,
    enabled: !!params.id,
  });

  const handleBlock = async () => {
    const token = localStorage.getItem("ayrshire_token");
    const confirmed = window.confirm(
      isBlocked
        ? `Unblock ${profile?.name ?? "this user"}? They will be able to message you again.`
        : `Block ${profile?.name ?? "this user"}? They won't be able to message you and their posts will be hidden from your community feed.`
    );
    if (!confirmed) return;
    setBlockLoading(true);
    try {
      await fetch(`/api/users/${params.id}/block`, {
        method: isBlocked ? "DELETE" : "POST",
        headers: { Authorization: `Bearer ${token ?? ""}` },
      });
      setIsBlocked(!isBlocked);
      if (!isBlocked) setLocation("/community");
    } catch { /* ignore */ } finally {
      setBlockLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-primary h-40 animate-pulse" />
        <div className="max-w-2xl mx-auto px-4 -mt-12 space-y-4">
          <div className="bg-white rounded-2xl p-6 animate-pulse space-y-3">
            <div className="w-20 h-20 rounded-2xl bg-slate-200" />
            <div className="h-5 bg-slate-200 rounded w-32" />
            <div className="h-3 bg-slate-200 rounded w-48" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500">Profile not found.</p>
          <button onClick={() => setLocation("/community")} className="mt-3 text-primary font-semibold text-sm hover:underline">← Back to Community</button>
        </div>
      </div>
    );
  }

  const totalReactions = profile.posts.reduce((s, p) => s + Object.values(p.reactions ?? {}).reduce((a, b) => a + b, 0), 0);
  const isOwnProfile = user?.id === profile.id;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header banner */}
      <div className="bg-primary h-32 relative">
        <button
          onClick={() => setLocation("/community")}
          className="absolute top-4 left-4 w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center text-white hover:bg-white/25 transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
      </div>

      <div className="max-w-2xl mx-auto px-4">
        {/* Profile card */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm -mt-12 mb-4 p-6 relative">
          {/* Avatar */}
          <div className="flex items-start justify-between mb-4">
            <WebAvatar name={profile.name} avatarUrl={profile.avatarUrl} size={80} shape="rounded" className="border-4 border-white shadow-sm" />
            <div className="flex items-center gap-2">
            {isOwnProfile && (
              <button
                onClick={() => setLocation("/edit-profile")}
                className="text-sm font-semibold text-primary border border-primary px-3 py-1.5 rounded-xl hover:bg-primary/5 transition-colors"
              >Edit Profile</button>
            )}
            {!isOwnProfile && user && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleBlock}
                  disabled={blockLoading}
                  title={isBlocked ? "Unblock user" : "Block user"}
                  className="flex items-center gap-1 text-sm font-semibold border border-slate-200 text-slate-500 px-3 py-1.5 rounded-xl hover:bg-slate-50 hover:text-slate-700 transition-colors"
                >
                  <SlashIcon size={14} />
                  {isBlocked ? "Unblock" : "Block"}
                </button>
                <button
                  onClick={async () => {
                    const token = localStorage.getItem("ayrshire_token");
                    try {
                      const res = await fetch("/api/conversations/direct", {
                        method: "POST",
                        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                        body: JSON.stringify({ userId: profile.id }),
                      });
                      const data = await res.json() as { id?: number };
                      if (data.id) setLocation(`/messages?conversationId=${data.id}`);
                    } catch { /* ignore */ }
                  }}
                  className="flex items-center gap-1.5 text-sm font-semibold bg-primary text-white px-4 py-1.5 rounded-xl hover:bg-primary/90 transition-colors"
                >
                  <MessageCircle size={14} />
                  Message
                </button>
              </div>
            )}
          </div>
          </div>

          <h1 className="text-xl font-bold text-slate-800">{profile.name}</h1>

          <div className="flex flex-wrap items-center gap-3 mt-1 mb-3">
            {profile.town && (
              <span className="flex items-center gap-1 text-sm text-slate-500">
                <MapPin size={13} /> {profile.town}
              </span>
            )}
            <span className="flex items-center gap-1 text-sm text-slate-500">
              <Calendar size={13} /> Member since {formatDate(profile.createdAt)}
            </span>
          </div>

          <div className="flex flex-wrap gap-2 mb-3">
            {profile.role === "admin" && (
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">Admin</span>
            )}
            {profile.isModerator && (
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">Mod</span>
            )}
            {profile.isJournalist && (
              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-sky-100 text-sky-700">Journalist</span>
            )}
            <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary capitalize">
              {profile.role === "tradesperson" ? "Tradesperson" : profile.role === "business" ? "Business" : profile.role === "admin" ? "Admin" : "Resident"}
            </span>
          </div>

          {profile.bio && (
            <p className="text-slate-600 text-sm leading-relaxed">{profile.bio}</p>
          )}

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-slate-100">
            <div className="text-center">
              <p className="font-bold text-slate-800 text-lg">{profile.posts.length}</p>
              <p className="text-xs text-slate-400">Posts</p>
            </div>
            <div className="text-center">
              <p className="font-bold text-slate-800 text-lg">{totalReactions}</p>
              <p className="text-xs text-slate-400">Reactions</p>
            </div>
            <div className="text-center">
              <p className="font-bold text-slate-800 text-lg">{profile.commentsMade}</p>
              <p className="text-xs text-slate-400">Comments</p>
            </div>
          </div>
        </div>

        {/* Posts */}
        <div className="pb-8">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Recent Posts</h2>
          {profile.posts.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
              <p className="text-slate-400 text-sm">{isOwnProfile ? "You haven't posted anything yet." : `${profile.name} hasn't posted anything yet.`}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {profile.posts.map((post) => {
                const total = Object.values(post.reactions ?? {}).reduce((a, b) => a + b, 0);
                return (
                  <div key={post.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <MapPin size={11} /> {post.town}
                      </span>
                      <span className="text-xs text-slate-300">·</span>
                      <span className="text-xs text-slate-400">{formatTimeAgo(post.createdAt)}</span>
                    </div>
                    <p className="text-slate-700 text-sm leading-relaxed">{post.content}</p>
                    {post.imageUrl && (
                      <div className="mt-2 rounded-xl overflow-hidden bg-slate-100">
                        <img src={post.imageUrl} alt="" className="w-full object-cover max-h-48" />
                      </div>
                    )}
                    <div className="flex items-center gap-4 mt-2 pt-2 border-t border-slate-50">
                      <span className="flex items-center gap-1 text-xs text-slate-400">❤️ {total}</span>
                      <span className="flex items-center gap-1 text-xs text-slate-400">
                        <MessageCircle size={12} /> {post.commentsCount}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
